import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { Badge, rsvpLabel, rsvpTone } from '../ui/Badge';

interface Calendar { id: string; name: string; color: string; timezone: string }
interface Attendee { email: string; name?: string | null; rsvp: string }
interface EventRow {
  id: string;
  occurrenceId?: string;
  summary: string;
  startsAt: string;
  endsAt: string;
  location?: string | null;
  attendees: Attendee[];
  calendar: Calendar;
  status: string;
}

interface AttendeeDraft { email: string; name?: string }

export function EventsPage() {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const today = new Date();
  const defaultStart = roundToNextQuarter(today);
  const defaultEnd = new Date(defaultStart.getTime() + 60 * 60 * 1000);
  const initialForm = {
    calendarId: '',
    summary: '',
    description: '',
    location: '',
    startsAt: toLocalInput(defaultStart),
    endsAt: toLocalInput(defaultEnd),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    allDay: false,
    recurrence: 'NONE',
    sendInvitations: true,
  };
  const [form, setForm] = useState(initialForm);
  const [attendeesInput, setAttendeesInput] = useState('');
  const [remindersInput, setRemindersInput] = useState('15:NOTIFICATION');

  const load = async () => {
    const [cals, evs] = await Promise.all([
      api.get<Calendar[]>('/api/calendars'),
      api.get<EventRow[]>('/api/events'),
    ]);
    setCalendars(cals);
    setEvents(evs);
    if (!form.calendarId && cals[0]) setForm((f) => ({ ...f, calendarId: cals[0].id }));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const attendees: AttendeeDraft[] = parseAttendees(attendeesInput);
    try {
      await api.post('/api/events', {
        ...form,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        rrule: recurrenceToRrule(form.recurrence),
        attendees,
        reminders: parseReminders(remindersInput),
      });
      toast.success(
        attendees.length > 0
          ? `Termin angelegt. ${attendees.length} Einladung(en) gehen raus.`
          : 'Termin angelegt.',
      );
      setOpen(false);
      setAttendeesInput('');
      setRemindersInput('15:NOTIFICATION');
      setForm(initialForm);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Fehler beim Anlegen');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-h3">Termine</h1>
        <button className="btn btn-primary" onClick={() => setOpen(true)} disabled={calendars.length === 0}>
          + Neuer Termin
        </button>
      </div>

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-body">
          <thead className="bg-surface-elevated text-meta uppercase tracking-[0.16em] text-ink-muted">
            <tr>
              <th className="text-left px-4 py-3">Titel</th>
              <th className="text-left px-4 py-3">Wann</th>
              <th className="text-left px-4 py-3">Kalender</th>
              <th className="text-left px-4 py-3">Teilnehmer</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const accepted = e.attendees.filter((a) => a.rsvp === 'ACCEPTED').length;
              const declined = e.attendees.filter((a) => a.rsvp === 'DECLINED').length;
              const tentative = e.attendees.filter((a) => a.rsvp === 'TENTATIVE').length;
              const pending = e.attendees.length - accepted - declined - tentative;
              return (
                <tr key={e.occurrenceId ?? e.id} className="table-row">
                  <td className="px-4 py-3">
                    <Link to={`/events/${e.id}`} className="font-medium hover:underline">{e.summary}</Link>
                    {e.location && <div className="text-small text-ink-muted">{e.location}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {new Date(e.startsAt).toLocaleString('de-DE')}
                    <div className="text-small text-ink-muted">{new Date(e.endsAt).toLocaleString('de-DE')}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: e.calendar.color }} />
                      {e.calendar.name}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {accepted > 0 && <Badge tone="success">{accepted} ja</Badge>}
                      {declined > 0 && <Badge tone="danger">{declined} nein</Badge>}
                      {tentative > 0 && <Badge tone="warning">{tentative} vielleicht</Badge>}
                      {pending > 0 && <Badge tone="muted">{pending} offen</Badge>}
                      {e.attendees.length === 0 && <span className="text-ink-muted text-small">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={e.status === 'CANCELLED' ? 'danger' : e.status === 'TENTATIVE' ? 'warning' : 'info'}>
                      {e.status.toLowerCase()}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {events.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-ink-muted py-8 text-small">Noch keine Termine.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Neuer Termin" size="lg" footer={
        <>
          <button className="btn btn-ghost" onClick={() => setOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleCreate}>Speichern</button>
        </>
      }>
        <form onSubmit={handleCreate} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Titel</label>
            <input className="input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required />
          </div>
          <div>
            <label className="label">Kalender</label>
            <select className="select" value={form.calendarId} onChange={(e) => setForm({ ...form, calendarId: e.target.value })}>
              {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Zeitzone</label>
            <input className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </div>
          <div>
            <label className="label">Start</label>
            <input type="datetime-local" className="input" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} required />
          </div>
          <div>
            <label className="label">Ende</label>
            <input type="datetime-local" className="input" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} required />
          </div>
          <label className="flex items-center gap-2 text-body">
            <input type="checkbox" checked={form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked })} />
            Ganztags
          </label>
          <div>
            <label className="label">Wiederholung</label>
            <select className="select" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}>
              <option value="NONE">Keine</option>
              <option value="DAILY">Taeglich</option>
              <option value="WEEKLY">Woechentlich</option>
              <option value="MONTHLY">Monatlich</option>
              <option value="YEARLY">Jaehrlich</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Ort</label>
            <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Beschreibung</label>
            <textarea className="textarea" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Teilnehmer (E-Mail pro Zeile, oder "Name &lt;email@x&gt;")</label>
            <textarea className="textarea font-mono" rows={4} value={attendeesInput} onChange={(e) => setAttendeesInput(e.target.value)} placeholder="kunde@example.com&#10;Anna Schmidt <anna@example.com>" />
          </div>
          <div className="col-span-2">
            <label className="label">Erinnerungen</label>
            <ReminderPicker value={remindersInput} onChange={setRemindersInput} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <input id="send" type="checkbox" checked={form.sendInvitations} onChange={(e) => setForm({ ...form, sendInvitations: e.target.checked })} />
            <label htmlFor="send" className="text-body text-ink-secondary">Einladungsmails sofort versenden</label>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function roundToNextQuarter(d: Date): Date {
  const out = new Date(d);
  out.setMinutes(Math.ceil(out.getMinutes() / 15) * 15);
  out.setSeconds(0);
  out.setMilliseconds(0);
  return out;
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseAttendees(input: string): AttendeeDraft[] {
  return input
    .split(/[\n,;]/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^(.+?)\s*<(.+@.+)>$/);
      if (m) return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
      return { email: line.toLowerCase() };
    })
    .filter((a) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email));
}

export { rsvpLabel, rsvpTone };

function ReminderPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selected = new Set(value.split(',').filter(Boolean));
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).join(','));
  };
  const options = [
    ['0:NOTIFICATION', 'Zum Start'],
    ['5:NOTIFICATION', '5 Min.'],
    ['15:NOTIFICATION', '15 Min.'],
    ['60:NOTIFICATION', '1 Std.'],
    ['1440:NOTIFICATION', '1 Tag'],
    ['15:EMAIL', '15 Min. Mail'],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([key, label]) => (
        <button key={key} type="button" className={`rounded-md border px-3 py-2 text-small ${selected.has(key) ? 'border-brand-primary/50 bg-brand-primary/15 text-ink-primary' : 'border-border bg-surface-elevated text-ink-muted'}`} onClick={() => toggle(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function parseReminders(input: string) {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [minutes, channel] = item.split(':');
      return { minutesBefore: Number(minutes), channel: channel === 'EMAIL' ? 'EMAIL' : 'NOTIFICATION' };
    })
    .filter((item) => Number.isFinite(item.minutesBefore));
}

function recurrenceToRrule(value: string): string | null {
  if (value === 'DAILY') return 'FREQ=DAILY';
  if (value === 'WEEKLY') return 'FREQ=WEEKLY';
  if (value === 'MONTHLY') return 'FREQ=MONTHLY';
  if (value === 'YEARLY') return 'FREQ=YEARLY';
  return null;
}
